/**
 * Calin Shoppable Videos — slider + modal with variant ATC
 */
(function () {
  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      const format =
        window.theme?.moneyFormat ||
        document.documentElement.dataset.moneyFormat ||
        '${{amount}}';
      return Shopify.formatMoney(cents, format);
    }
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const amount = (Number(cents) / 100).toFixed(2);
    return `$${amount}`;
  }

  function findVariant(product, selectedOptions) {
    return product.variants.find((variant) =>
      selectedOptions.every((val, i) => variant.options[i] === val)
    );
  }

  function variantImageUrl(variant, product) {
    const src =
      variant?.featured_image?.src ||
      variant?.featured_image ||
      product?.featured_image;
    if (!src) return null;
    return String(src).startsWith('//') ? `https:${src}` : src;
  }

  class CalinShoppableVideos extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.sectionId = this.dataset.sectionId;
      this.initSliders();
      this.bindSlideInteractions();
      this.bindModal();
      this.bindCartClose();
    }

    disconnectedCallback() {
      this.mainSlider?.destroy(true, true);
      this.modalSlider?.destroy(true, true);
      this.mainSlider = null;
      this.modalSlider = null;
      this._cartUnsub?.();
      this._cartUnsub = null;
      if (this._escapeHandler) {
        document.removeEventListener('keydown', this._escapeHandler);
      }
      this._initialized = false;
    }

    initSliders() {
      if (!window.Swiper) {
        requestAnimationFrame(() => this.initSliders());
        return;
      }

      const gap = parseInt(this.dataset.gap, 10) || 16;
      const gapMobile = parseInt(this.dataset.gapMobile, 10) || 16;
      const view = parseFloat(this.dataset.slidesDesktop) || 5;
      const viewMobile = parseFloat(this.dataset.slidesMobile) || 1.3;

      const mainEl = this.querySelector('.calin-sv__slider');
      if (mainEl) {
        this.mainSlider = new Swiper(mainEl, {
          speed: 300,
          spaceBetween: gapMobile,
          slidesPerView: viewMobile,
          breakpoints: {
            990: {
              spaceBetween: gap,
              slidesPerView: view,
            },
          },
        });
      }

      const modalEl = this.querySelector('.calin-sv__modal-slider');
      if (modalEl) {
        this.modalSlider = new Swiper(modalEl, {
          speed: 300,
          slidesPerView: 1,
          spaceBetween: 20,
          allowTouchMove: false,
          navigation: {
            nextEl: this.querySelector('.calin-sv__modal-next'),
            prevEl: this.querySelector('.calin-sv__modal-prev'),
          },
          on: {
            slideChange: () => this.onModalSlideChange(),
          },
        });
      }
    }

    getProductData(blockId) {
      const el = this.querySelector(`[data-calin-sv-product="${blockId}"]`);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch {
        return null;
      }
    }

    bindSlideInteractions() {
      this.querySelectorAll('.calin-sv__slide').forEach((slide) => {
        const blockId = slide.dataset.blockId;
        const slideIndex = parseInt(slide.dataset.slideIndex, 10);
        const hasVariants = slide.dataset.hasVariants === 'true';

        slide.querySelectorAll('[data-open-modal-slide]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openModal(slideIndex);
          });
        });

        const media = slide.querySelector('[data-open-modal]');
        if (media) {
          media.addEventListener('click', () => this.openModal(slideIndex));
        }

        if (!hasVariants) return;

        const modalSlide = this.querySelector(
          `.calin-sv__modal-slide[data-block-id="${blockId}"]`
        );
        const product = this.getProductData(blockId);
        if (!modalSlide || !product) return;

        const selects = modalSlide.querySelectorAll('.calin-sv__select');
        const updateModalVariant = () => {
          const selected = Array.from(selects).map((s) => s.value);
          const variant = findVariant(product, selected);
          if (variant) this.applyVariantToModal(modalSlide, product, variant);
        };

        selects.forEach((select) => {
          select.addEventListener('change', updateModalVariant);
        });
      });
    }

    applyVariantToModal(modalSlide, product, variant) {
      const modalInput = modalSlide.querySelector('[data-variant-input]');
      const modalPrice = modalSlide.querySelector('[data-modal-price]');
      const modalOptionPrice = modalSlide.querySelector('[data-modal-option-price]');
      const modalImage = modalSlide.querySelector('[data-modal-image]');
      const modalAtc = modalSlide.querySelector('[data-modal-atc]');
      const atcText = modalAtc?.querySelector('[data-addtocart-text]');
      const price = formatMoney(variant.price);

      if (modalInput) modalInput.value = variant.id;
      if (modalPrice) modalPrice.textContent = price;
      if (modalOptionPrice) modalOptionPrice.textContent = price;

      const imgUrl = variantImageUrl(variant, product);
      if (modalImage && imgUrl) modalImage.src = imgUrl;

      if (modalAtc) {
        modalAtc.disabled = !variant.available;
        const label = modalAtc.dataset.label || 'Add to cart';
        if (atcText) {
          atcText.textContent = variant.available ? label : 'Sold out';
        } else {
          modalAtc.textContent = variant.available ? label : 'Sold out';
        }
      }
    }

    bindModal() {
      this.modal = this.querySelector('.calin-sv__modal');
      const closeBtn = this.querySelector('.calin-sv__modal-close');

      closeBtn?.addEventListener('click', () => this.closeModal());
      this.modal?.addEventListener('click', (e) => {
        if (e.target === this.modal) this.closeModal();
      });

      this._escapeHandler = (e) => {
        if (e.key === 'Escape' && this.modal?.classList.contains('is-active')) {
          this.closeModal();
        }
      };
      document.addEventListener('keydown', this._escapeHandler);

      this.querySelectorAll('.calin-sv__mute').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const video = btn.closest('.calin-sv__modal-media')?.querySelector('video');
          if (!video) return;
          video.muted = !video.muted;
          btn.classList.toggle('is-unmuted', !video.muted);
        });
      });
    }

    bindCartClose() {
      document.addEventListener('shopify:cart:lines-update', () => {
        if (!this.modal?.classList.contains('is-active')) return;
        this.closeModal();
      });
    }

    openModal(index) {
      if (!this.modalSlider || !this.modal) return;
      this.modalSlider.slideTo(index);
      this.modal.classList.add('is-active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.onModalSlideChange();
    }

    closeModal() {
      this.modal?.classList.remove('is-active');
      this.modal?.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.querySelectorAll('.calin-sv__modal-media video').forEach((v) => {
        v.pause();
        v.muted = true;
      });
      this.querySelectorAll('.calin-sv__mute').forEach((b) => b.classList.remove('is-unmuted'));
    }

    onModalSlideChange() {
      if (!this.modalSlider) return;
      this.querySelectorAll('.calin-sv__modal-media video').forEach((v) => {
        v.pause();
        v.muted = true;
      });

      const active = this.modalSlider.slides[this.modalSlider.activeIndex];
      if (!active) return;

      const video = active.querySelector('.calin-sv__modal-media video');
      const muteBtn = active.querySelector('.calin-sv__mute');
      if (video) {
        video.muted = false;
        video.play().catch(() => {});
        muteBtn?.classList.add('is-unmuted');
      }
    }
  }

  if (!customElements.get('calin-shoppable-videos')) {
    customElements.define('calin-shoppable-videos', CalinShoppableVideos);
  }
})();
